import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  BarChart3, 
  TrendingUp, 
  Calendar,
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
  Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Reports = () => {
  const [dailyReports, setDailyReports] = useState([]);
  const [networkReport, setNetworkReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [dailyRes, networkRes, txRes] = await Promise.all([
        axios.get(`${API}/reports/daily?days=7`, { withCredentials: true }),
        axios.get(`${API}/reports/by-network?days=30`, { withCredentials: true }),
        axios.get(`${API}/transactions?limit=1000`, { withCredentials: true })
      ]);
      setDailyReports(dailyRes.data);
      setNetworkReport(networkRes.data);
      setTransactions(txRes.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (transactions.length === 0) return;
    
    const headers = ['Transaction ID', 'Amount (LRD)', 'Description', 'Reference', 'Network', 'Status', 'Created By', 'Date'];
    const rows = transactions.map(tx => [
      tx.transaction_id,
      tx.amount,
      `"${tx.description?.replace(/"/g, '""') || ''}"`,
      tx.reference,
      tx.network,
      tx.status,
      tx.created_by_name,
      new Date(tx.created_at).toLocaleString()
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate totals from daily reports
  const totals = dailyReports.reduce((acc, day) => ({
    transactions: acc.transactions + day.total_transactions,
    amount: acc.amount + day.total_amount,
    confirmed: acc.confirmed + day.confirmed_count,
    confirmedAmount: acc.confirmedAmount + day.confirmed_amount
  }), { transactions: 0, amount: 0, confirmed: 0, confirmedAmount: 0 });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0077B6]" />
      </div>
    );
  }

  return (
    <div data-testid="reports-page" className="animate-fadeIn">
      {/* Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
        .hover-lift { transition: all 0.3s ease; }
        .hover-lift:hover { transform: translateY(-3px); box-shadow: 0 12px 24px -8px rgba(0,0,0,0.12); }
        .stagger-1 { animation-delay: 50ms; }
        .stagger-2 { animation-delay: 100ms; }
        .stagger-3 { animation-delay: 150ms; }
        .stagger-4 { animation-delay: 200ms; }
      `}</style>
      
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A1628]">Reports</h1>
          <p className="text-slate-500 mt-1">
            View your transaction analytics and daily totals
          </p>
        </div>
        <Button
          onClick={exportToCSV}
          disabled={transactions.length === 0}
          className="bg-[#0077B6] hover:bg-[#0096C7] text-white transition-all duration-200 hover:scale-105 active:scale-95"
          data-testid="export-csv-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover-lift animate-fadeIn stagger-1 cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-500 text-sm">7-Day Transactions</span>
            <BarChart3 className="w-5 h-5 text-[#0077B6] transition-transform duration-300 hover:scale-125" />
          </div>
          <div className="text-3xl font-bold text-[#0A1628]">{totals.transactions}</div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover-lift animate-fadeIn stagger-2 cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-500 text-sm">7-Day Volume</span>
            <TrendingUp className="w-5 h-5 text-emerald-600 transition-transform duration-300 hover:scale-125" />
          </div>
          <div className="text-3xl font-bold text-[#0A1628]">LRD {formatCurrency(totals.amount)}</div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover-lift animate-fadeIn stagger-3 cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-500 text-sm">Confirmed</span>
            <CheckCircle2 className="w-5 h-5 text-emerald-600 transition-transform duration-300 hover:scale-125" />
          </div>
          <div className="text-3xl font-bold text-emerald-600">{totals.confirmed}</div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover-lift animate-fadeIn stagger-4 cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-500 text-sm">Confirmed Volume</span>
            <CheckCircle2 className="w-5 h-5 text-emerald-600 transition-transform duration-300 hover:scale-125" />
          </div>
          <div className="text-3xl font-bold text-emerald-600">LRD {formatCurrency(totals.confirmedAmount)}</div>
        </div>
      </div>

      {/* Network Breakdown */}
      {networkReport && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8 hover-lift">
          <h2 className="text-lg font-semibold text-[#0A1628] mb-6">
            Network Breakdown (Last 30 Days)
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 rounded-xl bg-yellow-50 border border-yellow-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer">
              <div className="flex items-center justify-between mb-4">
                <span className="text-lg font-semibold text-yellow-800">MTN Mobile Money</span>
                <span className="text-sm bg-yellow-200 text-yellow-800 px-2 py-1 rounded">
                  {networkReport.MTN.count} transactions
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-yellow-700">Total Volume</span>
                  <span className="font-semibold text-yellow-800">
                    LRD {formatCurrency(networkReport.MTN.total_amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-yellow-700">Confirmed</span>
                  <span className="font-semibold text-yellow-800">
                    LRD {formatCurrency(networkReport.MTN.confirmed_amount)}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-xl bg-orange-50 border border-orange-200">
              <div className="flex items-center justify-between mb-4">
                <span className="text-lg font-semibold text-orange-800">Orange Money</span>
                <span className="text-sm bg-orange-200 text-orange-800 px-2 py-1 rounded">
                  {networkReport.Orange.count} transactions
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-orange-700">Total Volume</span>
                  <span className="font-semibold text-orange-800">
                    LRD {formatCurrency(networkReport.Orange.total_amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-orange-700">Confirmed</span>
                  <span className="font-semibold text-orange-800">
                    LRD {formatCurrency(networkReport.Orange.confirmed_amount)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Daily Totals Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#0077B6]" />
            <h2 className="text-lg font-semibold text-[#0A1628]">Daily Totals (Last 7 Days)</h2>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full" data-testid="daily-reports-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">
                  Date
                </th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">
                  Transactions
                </th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">
                  Total Amount
                </th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">
                  Confirmed
                </th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">
                  Pending
                </th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">
                  Failed
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {dailyReports.map((day) => (
                <tr key={day.date} className="hover:bg-slate-50">
                  <td className="px-6 py-4 font-medium text-slate-700">
                    {formatDate(day.date)}
                  </td>
                  <td className="px-6 py-4 text-right text-slate-700">
                    {day.total_transactions}
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-slate-700">
                    LRD {formatCurrency(day.total_amount)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="inline-flex items-center gap-1 text-emerald-600">
                      <CheckCircle2 className="w-4 h-4" />
                      {day.confirmed_count}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="inline-flex items-center gap-1 text-amber-600">
                      <Clock className="w-4 h-4" />
                      {day.pending_count}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="inline-flex items-center gap-1 text-red-600">
                      <XCircle className="w-4 h-4" />
                      {day.failed_count}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Reports;
